import { CartItem, SubmitCartItem } from '@/Model/checkout/cart/types';
import { Scholar } from '@/Model/enrollment/types';
import APP_UTILITIES from '@/utilities/commonFunctions';

export function sortSubmitCartItems(cartItems: Array<CartItem>): Array<SubmitCartItem> {
  const unorderedCartItems = Object.values(
    cartItems.reduce((map, item) => {
      map[item.session.sessionId] = map[item.session.sessionId] || {
        session: item.session,
        cartItemIds: [],
        scholars: []
      };
      map[item.session.sessionId].cartItemIds.push(item.cartItemId);
      if (item.scholar) {
        map[item.session.sessionId].scholars.push(item.scholar);
      }
      return map;
    }, {} as { [sessionId: number]: SubmitCartItem })
  );

  const orderedCartItems = unorderedCartItems.sort((a,b) => {
    return a.cartItemIds.sort((a,b) => {
      return a-b;
    })[0] - b.cartItemIds.sort((a,b) => {
      return a-b;
    })[0];
  });

  return orderedCartItems;
}

// This function is based off a similar BE method in api-enrollment in Bell.Enrollment.Helper/Payments/ExternalPaymentsCommon.cs
/**
 * Calculates an array of payments based on the passed total dispersed over the specified number of payment installments.
 * @param totalAmount The total price amount, in dollars, that will be divided over the installments. Must be greater than `0`.
 * @param installmentCount The number of payment installments to disperse the total amount over. Must be at least `2`.
 * @returns An array with the installment values, with the last item in the array being the final (make-up) installment value if the payments are not evenly distributable.
 */
export function getInstallmentsWithMakeupOnFinalInstallment(totalAmount: number, installmentCount: number): Array<number> {
  if (installmentCount < 2) {
    throw new RangeError('`installmentCount` must be at least 2');
  }

  if (totalAmount <= 0) {
    throw new RangeError('`totalAmount` must be greater than 0');
  }

  const totalAmountInCents = totalAmount * 100;
  const installmentCountMinusOne = installmentCount - 1;

  let typicalInstallmentInCents = Math.trunc(totalAmountInCents / installmentCount);
  let finalInstallmentInCents = totalAmountInCents - (typicalInstallmentInCents * installmentCountMinusOne);

  if (typicalInstallmentInCents > finalInstallmentInCents) {
    typicalInstallmentInCents = finalInstallmentInCents;
    finalInstallmentInCents = totalAmountInCents - (typicalInstallmentInCents * installmentCountMinusOne);
  }

  if (typicalInstallmentInCents <= 0) {
    throw new RangeError('Resulting `typicalInstallment` value is less than or equal to 0');
  }

  const typicalInstallmentInDollars = Math.trunc(typicalInstallmentInCents) / 100;
  const finalInstallmentInDollars = Math.trunc(finalInstallmentInCents) / 100;

  const installmentsInDollars = [
    ...new Array(installmentCountMinusOne).fill(typicalInstallmentInDollars), // fill indices 0 thru n-2 cycles with typical value
    finalInstallmentInDollars
  ];

  return installmentsInDollars;
}

export function doScholarsMatch(scholarA: Scholar, scholarB: Scholar) {
  const formatBirthdate = (birthdate: string) => {
    const requiredBirthdateFormat = /\d{2} ?- ?\d{2} ?- ?\d{4}/;
    if (birthdate.match(requiredBirthdateFormat)) {
      const formattedBirthdate = `${APP_UTILITIES.formatShortDateFullYear(birthdate.replace(/\s/g, ''))} 00:00`;
      return formattedBirthdate;
    }
    return birthdate;
  };

  const isMatchingScholars = (
    scholarA.birthDate && scholarB.birthDate &&
    scholarA.firstName.toLowerCase() === scholarB.firstName.toLowerCase() &&
    scholarA.lastName.toLowerCase() === scholarB.lastName.toLowerCase() &&
    formatBirthdate(scholarA.birthDate) === formatBirthdate(scholarB.birthDate)
  );

  return isMatchingScholars;
}